import { useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, GitBranch, Pencil, Plus, Search, Trash2 } from 'lucide-react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { SearchableSelect } from '@/components/common/searchable-select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import type { WorkspacePortalMode } from '@/lib/workspace-labels'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import { getUserDropdownKeywords, getUserDropdownLabel } from '@/lib/user-display'
import { cn } from '@/lib/utils'
import { catalogRolesApi, type CatalogRole } from '@/services/catalog-roles'
import jobWorkflowApi from '@/services/job-workflow'
import { orgRolesApi, type OrgRole } from '@/services/org-roles'
import { orgUserAdminApi, type OrgUserAdminRecord } from '@/services/org-user-admin'
import type { JobWorkflowKind } from '@/services/job-workflow'
import { postsApi, type AssignedRolePost } from '@/services/posts'
import type {
  JobKind,
  JobStage,
  JobStageDefinition,
  JobStageTransition,
  JobWorkflowEntityType,
  JobWorkflowStatus,
  JobWorkflowStatusDefinition,
  JobWorkflowTransition,
} from '@/types'

type ResourceKey =
  | 'kinds'
  | 'childRules'
  | 'statusDefinitions'
  | 'statuses'
  | 'transitions'
  | 'transitionActors'
  | 'stageDefinitions'
  | 'stages'
  | 'stageTransitions'
  | 'stageTransitionActors'

type WorkflowRecord = Record<string, unknown> & { id?: string | number }
type WorkflowFormState = Record<string, string | boolean | number | null>
type WorkflowFormPatch = Record<string, string | boolean | number | null>
type WorkflowResourceApi = {
  create: (payload: WorkflowRecord) => Promise<unknown>
  update: (id: string | number, payload: WorkflowRecord) => Promise<unknown>
  delete: (id: string | number) => Promise<void>
}

type FieldOption = {
  value: string
  label: string
  keywords?: string
  disabled?: boolean
}

type FieldConfig = {
  name: string
  label: string
  type?: 'text' | 'textarea' | 'number' | 'boolean' | 'select'
  options?: FieldOption[]
  searchable?: boolean
  searchPlaceholder?: string
  emptyText?: string
  emptySearchText?: string
  loading?: boolean
  disabled?: boolean
  onOpenChange?: (open: boolean) => void
  onSearchChange?: (value: string) => void
  required?: boolean
  requiredWhen?: (form: WorkflowFormState) => boolean
  visibleWhen?: (form: WorkflowFormState) => boolean
}

type ResourceConfig = {
  key: ResourceKey
  label: string
  singular: string
  searchFields: string[]
  columns: Array<{ key: string; label: string; render?: (record: WorkflowRecord) => ReactNode }>
  fields: (lookups: LookupData, form?: WorkflowFormState) => FieldConfig[]
  defaults: WorkflowFormState
  buildPayload: (form: WorkflowFormState) => WorkflowRecord
}

type LookupData = {
  kinds: JobKind[]
  statusDefinitions: JobWorkflowStatusDefinition[]
  statuses: JobWorkflowStatus[]
  transitions: JobWorkflowTransition[]
  stageDefinitions: JobStageDefinition[]
  stages: JobStage[]
  stageTransitions: JobStageTransition[]
  actorOptions: {
    roles: FieldOption[]
    posts: FieldOption[]
    users: FieldOption[]
    workerSkills: FieldOption[]
  }
}

type ActorLookupField = 'role' | 'post' | 'user' | 'worker_skill'

const ENTITY_TYPES: Array<{ value: JobWorkflowEntityType; label: string }> = [
  { value: 'job', label: 'Job' },
  { value: 'assignment', label: 'Assignment' },
  { value: 'workday_entry', label: 'Workday Entry' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'task', label: 'Task' },
  { value: 'expense', label: 'Expense' },
  { value: 'inventory', label: 'Inventory' },
]

const ENTITY_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  ENTITY_TYPES.map((entityType) => [entityType.value, entityType.label]),
)

const ACTOR_TYPES: FieldOption[] = [
  { value: 'role', label: 'Role' },
  { value: 'post', label: 'Post' },
  { value: 'user', label: 'User' },
  { value: 'worker_skill', label: 'Skill' },
  { value: 'assigned_worker', label: 'Assigned worker' },
  { value: 'system', label: 'System' },
  { value: 'any_authenticated', label: 'Any authenticated user' },
]

const QUICK_FILTERS = [
  { value: 'job', label: 'Job' },
  { value: 'assignment', label: 'Assignment' },
  { value: 'workday_entry', label: 'Workday Entry' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'expense', label: 'Expense' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'project', label: 'Project' },
  { value: 'event', label: 'Event' },
  { value: 'work_order', label: 'Work Order' },
  { value: 'ask', label: 'Ask' },
] as const

type QuickFilterValue = typeof QUICK_FILTERS[number]['value']

const NONE_VALUE = '__none__'
const ENTITY_WORKFLOW_FILTERS = ['job', 'assignment', 'workday_entry', 'attendance', 'expense', 'inventory'] as const
const KIND_WORKFLOW_FILTERS = ['job', 'project', 'event', 'work_order', 'ask'] as const

const primaryEntityFilterLabel = (portalKind: JobWorkflowKind) => {
  if (portalKind === 'event') {
    return 'Event'
  }
  if (portalKind === 'project') {
    return 'Project'
  }
  return 'Job'
}

const resolvePortalWorkflowKind = (pathname: string, mode: WorkspacePortalMode): JobWorkflowKind => {
  if (mode === 'event' || pathname.includes('/events/')) {
    return 'event'
  }
  if (mode === 'project' || pathname.includes('/projects/')) {
    return 'project'
  }
  return 'job'
}

const entityQuickFilters = (portalKind: JobWorkflowKind) => ENTITY_WORKFLOW_FILTERS.map((value) => ({
  value,
  label: value === 'job' ? primaryEntityFilterLabel(portalKind) : QUICK_FILTERS.find((filter) => filter.value === value)?.label ?? value,
}))

const textValue = (value: unknown) => String(value ?? '').trim()
const workflowEntityTypeForFilter = (filter: QuickFilterValue): JobWorkflowEntityType => (
  filter === 'attendance' ? 'attendance' : filter as JobWorkflowEntityType
)
const workflowEntityTypeLabel = (value: unknown) => ENTITY_TYPE_LABELS[textValue(value)] || textValue(value)
const nullableId = (value: unknown) => {
  const normalized = textValue(value)
  return normalized && normalized !== NONE_VALUE ? normalized : null
}
const numberValue = (value: unknown, fallback = 0) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const getRecordLabel = (record: { id?: string | number; label?: unknown; code?: unknown; name?: unknown }, fallback = 'Untitled') => (
  textValue(record.label)
  || textValue(record.code)
  || textValue(record.name)
  || textValue(record.id)
  || fallback
)

const optionFromRecord = (record: { id?: string | number; label?: unknown; code?: unknown; name?: unknown }): FieldOption => ({
  value: String(record.id),
  label: getRecordLabel(record),
})

const kindOptions = (kinds: JobKind[], includeNone = true): FieldOption[] => [
  ...(includeNone ? [{ value: NONE_VALUE, label: 'All kinds' }] : []),
  ...kinds.map(optionFromRecord),
]

const statusOptions = (statuses: JobWorkflowStatus[], entityType?: string, includeNone = true): FieldOption[] => [
  ...(includeNone ? [{ value: NONE_VALUE, label: 'Start' }] : []),
  ...statuses
    .filter((status) => !entityType || status.entity_type === entityType)
    .map((status) => ({
      value: String(status.id),
      label: entityType ? textValue(status.label) : `${status.label} (${workflowEntityTypeLabel(status.entity_type)})`,
      keywords: `${status.label ?? ''} ${status.code ?? ''} ${status.entity_type ?? ''}`,
    })),
]

const stageOptions = (stages: JobStage[], includeNone = true): FieldOption[] => [
  ...(includeNone ? [{ value: NONE_VALUE, label: 'Start' }] : []),
  ...stages.map(optionFromRecord),
]

const statusTransitionOptions = (transitions: JobWorkflowTransition[]): FieldOption[] => transitions.map((transition) => ({
  value: String(transition.id),
  label: `${workflowEntityTypeLabel(transition.entity_type)}: ${transition.from_status_label || 'Start'} -> ${transition.to_status_label || transition.to_status}`,
}))

const stageTransitionOptions = (transitions: JobStageTransition[]): FieldOption[] => transitions.map((transition) => ({
  value: String(transition.id),
  label: `${transition.from_stage_label || 'Start'} -> ${transition.to_stage_label || transition.to_stage}`,
}))

const actorTargetLabel = (record: WorkflowRecord) => (
  textValue(record.role_label || record.post_label || record.user_label || record.worker_skill_label || record.role || record.post || record.user || record.worker_skill) || 'Any'
)

const isActorLookupField = (value: string): value is ActorLookupField => (
  value === 'role' || value === 'post' || value === 'user' || value === 'worker_skill'
)

const actorTargetFields = (lookups: LookupData): FieldConfig[] => [
  { name: 'role', label: 'Role', type: 'select', searchable: true, options: lookups.actorOptions.roles, requiredWhen: (form) => textValue(form.actor_type) === 'role', visibleWhen: (form) => textValue(form.actor_type) === 'role' },
  { name: 'post', label: 'Post', type: 'select', searchable: true, options: lookups.actorOptions.posts, requiredWhen: (form) => textValue(form.actor_type) === 'post', visibleWhen: (form) => textValue(form.actor_type) === 'post' },
  { name: 'user', label: 'User', type: 'select', searchable: true, options: lookups.actorOptions.users, requiredWhen: (form) => textValue(form.actor_type) === 'user', visibleWhen: (form) => textValue(form.actor_type) === 'user' },
  { name: 'worker_skill', label: 'Skill', type: 'select', searchable: true, options: lookups.actorOptions.workerSkills, requiredWhen: (form) => textValue(form.actor_type) === 'worker_skill', visibleWhen: (form) => textValue(form.actor_type) === 'worker_skill' },
]

const buildActorPayload = (form: WorkflowFormState) => {
  const actorType = textValue(form.actor_type) || 'any_authenticated'
  return {
    transition: nullableId(form.transition),
    actor_type: actorType,
    role: actorType === 'role' || actorType === 'worker_skill' ? nullableId(form.role || form.worker_skill) : null,
    post: actorType === 'post' ? nullableId(form.post) : null,
    user: actorType === 'user' ? nullableId(form.user) : null,
    worker_skill: null,
    active: Boolean(form.active),
  }
}

const badgeClassName = (active?: unknown) => active === false
  ? 'border-slate-300 bg-slate-100 text-slate-600'
  : 'border-emerald-200 bg-emerald-50 text-emerald-700'

function ActiveBadge({ active }: { active?: unknown }) {
  return (
    <Badge variant="outline" className={badgeClassName(active)}>
      {active === false ? 'Disabled' : 'Enabled'}
    </Badge>
  )
}

function BoolBadge({ value, label }: { value?: unknown; label: string }) {
  if (!value) {
    return null
  }
  return <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">{label}</Badge>
}

function SystemBadge({ record }: { record: WorkflowRecord }) {
  if (!record.system_protected) {
    return null
  }
  return <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">System</Badge>
}

const workflowScopeLabel = (record: WorkflowRecord) => {
  const parts = [
    textValue(record.kind_code || record.kind) || 'All kinds',
    record.service_variant ? `Service ${textValue(record.service_variant)}` : '',
    record.organisation ? `Org ${textValue(record.organisation)}` : '',
    record.branch ? `Branch ${textValue(record.branch)}` : '',
    record.category ? `Category ${textValue(record.category)}` : '',
  ].filter(Boolean)

  const scope = parts.join(' · ')
  return scope || 'Global'
}

const workflowScopeScore = (record: WorkflowRecord) => (
  ['kind', 'service_variant', 'organisation', 'branch', 'category']
    .filter((fieldName) => Boolean(record[fieldName])).length
)

const duplicateTransitionKey = (record: WorkflowRecord) => [
  textValue(record.entity_type),
  textValue(record.from_status || record.from_status_code || 'start'),
  textValue(record.to_status || record.to_status_code),
].join('|')

const duplicateStageTransitionKey = (record: WorkflowRecord) => [
  textValue(record.from_stage || record.from_stage_code || 'start'),
  textValue(record.to_stage || record.to_stage_code),
].join('|')

const preferWorkflowRecord = (current: WorkflowRecord, candidate: WorkflowRecord) => {
  if (current.active === false && candidate.active !== false) {
    return candidate
  }
  if (current.active !== false && candidate.active === false) {
    return current
  }

  const currentScopeScore = workflowScopeScore(current)
  const candidateScopeScore = workflowScopeScore(candidate)
  if (candidateScopeScore !== currentScopeScore) {
    return candidateScopeScore > currentScopeScore ? candidate : current
  }

  return numberValue(candidate.sorting_order) < numberValue(current.sorting_order) ? candidate : current
}

const dedupeTransitionRecords = (activeKey: ResourceKey, records: WorkflowRecord[]) => {
  if (activeKey !== 'transitions' && activeKey !== 'stageTransitions') {
    return records
  }

  const getKey = activeKey === 'transitions' ? duplicateTransitionKey : duplicateStageTransitionKey
  const byTransition = new Map<string, WorkflowRecord>()

  records.forEach((record) => {
    const key = getKey(record)
    const existing = byTransition.get(key)
    byTransition.set(key, existing ? preferWorkflowRecord(existing, record) : record)
  })

  return Array.from(byTransition.values())
}

function ScopeCell({ record }: { record: WorkflowRecord }) {
  return (
    <div className="space-y-1">
      <p className="text-sm text-foreground">{workflowScopeLabel(record)}</p>
      <p className="text-xs text-muted-foreground">Order {numberValue(record.sorting_order)}</p>
    </div>
  )
}

const isProtectedRecord = (record: WorkflowRecord | null) => Boolean(record?.system_protected)

const quickFilterMatches = (record: WorkflowRecord, filter: QuickFilterValue, portalKind: JobWorkflowKind) => {
  const entityType = textValue(record.entity_type)
  const kindCode = textValue(record.kind_code)
  const transitionLabel = textValue(record.transition_label).toLowerCase()

  if (kindCode && kindCode !== portalKind) {
    return false
  }

  if (filter === 'job') {
    return entityType === 'job'
  }

  if (filter === 'assignment' || filter === 'workday_entry' || filter === 'attendance' || filter === 'expense' || filter === 'inventory') {
    const backendEntityType = workflowEntityTypeForFilter(filter)
    return entityType === backendEntityType || transitionLabel.startsWith(`${backendEntityType}:`)
  }

  if (isKindWorkflowFilter(filter)) {
    return kindCode === filter
  }

  return false
}

const isEntityWorkflowFilter = (filter: QuickFilterValue): filter is typeof ENTITY_WORKFLOW_FILTERS[number] => (
  (ENTITY_WORKFLOW_FILTERS as readonly string[]).includes(filter)
)

const isKindWorkflowFilter = (filter: QuickFilterValue): filter is typeof KIND_WORKFLOW_FILTERS[number] => (
  (KIND_WORKFLOW_FILTERS as readonly string[]).includes(filter)
)

const normalizeWorkflowCode = (value: unknown) => textValue(value).toLowerCase().replace(/[\s-]+/g, '_')

const getKindIdForWorkflowFilter = (lookups: LookupData, filter: QuickFilterValue | JobWorkflowKind) => {
  if (!isKindWorkflowFilter(filter as QuickFilterValue) && filter !== 'job' && filter !== 'project' && filter !== 'event') {
    return NONE_VALUE
  }

  const matchingKind = lookups.kinds.find((kind) => (
    normalizeWorkflowCode(kind.code) === filter
    || normalizeWorkflowCode(kind.label) === filter
    || normalizeWorkflowCode(kind.route_key) === filter
  ))

  return matchingKind?.id != null ? String(matchingKind.id) : NONE_VALUE
}

const buildStatusAutoFields = (form: WorkflowFormState, lookups: LookupData): WorkflowFormPatch => {
  const definitionId = textValue(form.definition)
  const definition = lookups.statusDefinitions.find((item) => String(item.id) === definitionId)
  if (!definition) {
    return {}
  }

  const entityType = textValue(form.entity_type) || 'job'
  const kindId = textValue(form.kind)
  const kind = kindId && kindId !== NONE_VALUE
    ? lookups.kinds.find((item) => String(item.id) === kindId)
    : null
  const scopeCode = normalizeWorkflowCode(
    kind?.code
    || kind?.label
    || workflowEntityTypeLabel(entityType),
  )
  const statusCode = normalizeWorkflowCode(definition.code || definition.label)
  const label = textValue(definition.label || definition.code)

  return {
    code: [scopeCode, statusCode].filter(Boolean).join('_'),
    label,
  }
}

const buildScopedCreateDefaults = (
  config: ResourceConfig,
  filter: QuickFilterValue,
  portalKind: JobWorkflowKind,
  lookups: LookupData,
): WorkflowFormState => {
  const defaults = { ...config.defaults }
  const kind = getKindIdForWorkflowFilter(lookups, portalKind)

  if ('entity_type' in defaults) {
    defaults.entity_type = isEntityWorkflowFilter(filter) ? workflowEntityTypeForFilter(filter) : 'job'
  }

  if ('kind' in defaults) {
    defaults.kind = kind
  }

  return defaults
}

const shouldShowScopedField = (
  field: FieldConfig,
  activeKey: ResourceKey,
  isEditing: boolean,
) => {
  if (isEditing) {
    return true
  }

  if (field.name === 'entity_type' && ['statuses', 'transitions'].includes(activeKey)) {
    return false
  }

  if (field.name === 'kind') {
    if (['statuses', 'transitions', 'stages', 'stageTransitions'].includes(activeKey)) {
      return false
    }
    return false
  }

  return true
}

const scopeFieldOptions = (
  field: FieldConfig,
  activeKey: ResourceKey,
  filter: QuickFilterValue,
  portalKind: JobWorkflowKind,
  lookups: LookupData,
  isEditing: boolean,
): FieldConfig => {
  if (isEditing || field.name !== 'transition') {
    return field
  }

  if (activeKey === 'transitionActors') {
    return {
      ...field,
      options: statusTransitionOptions(
        lookups.transitions.filter((transition) => quickFilterMatches(transition as unknown as WorkflowRecord, filter, portalKind)),
      ),
    }
  }

  if (activeKey === 'stageTransitionActors') {
    return {
      ...field,
      options: stageTransitionOptions(
        lookups.stageTransitions.filter((transition) => quickFilterMatches(transition as unknown as WorkflowRecord, filter, portalKind)),
      ),
    }
  }

  return field
}

const makeDefinitionPayload = (form: WorkflowFormState) => ({
  code: textValue(form.code),
  label: textValue(form.label),
  description: textValue(form.description),
  is_system: Boolean(form.is_system),
  active: Boolean(form.active),
})

const RESOURCE_CONFIGS: ResourceConfig[] = [
  {
    key: 'kinds',
    label: 'Kinds',
    singular: 'kind',
    searchFields: ['code', 'label', 'plural_label', 'description'],
    columns: [
      { key: 'label', label: 'Kind', render: (record) => <div><p className="font-medium">{textValue(record.label)}</p><p className="text-xs text-muted-foreground">{textValue(record.code)}</p></div> },
      { key: 'route_key', label: 'Route' },
      { key: 'is_default', label: 'Flags', render: (record) => <div className="flex gap-1"><SystemBadge record={record} /><BoolBadge value={record.is_default} label="Default" /><ActiveBadge active={record.active} /></div> },
    ],
    defaults: { code: '', label: '', plural_label: '', description: '', icon: '', route_key: '', is_default: false, active: true },
    fields: () => [
      { name: 'code', label: 'Code', required: true },
      { name: 'label', label: 'Label', required: true },
      { name: 'plural_label', label: 'Plural label' },
      { name: 'route_key', label: 'Route key' },
      { name: 'icon', label: 'Icon' },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'is_default', label: 'Default kind', type: 'boolean' },
      { name: 'active', label: 'Active', type: 'boolean' },
    ],
    buildPayload: (form) => ({ ...form, code: textValue(form.code), label: textValue(form.label), active: Boolean(form.active), is_default: Boolean(form.is_default) }),
  },
  {
    key: 'childRules',
    label: 'Hierarchy',
    singular: 'child rule',
    searchFields: ['parent_kind_label', 'child_kind_label', 'parent_kind_code', 'child_kind_code'],
    columns: [
      { key: 'parent_kind_label', label: 'Parent', render: (record) => textValue(record.parent_kind_label || record.parent_kind) },
      { key: 'child_kind_label', label: 'Child', render: (record) => textValue(record.child_kind_label || record.child_kind) },
      { key: 'active', label: 'State', render: (record) => <div className="flex gap-1"><SystemBadge record={record} /><ActiveBadge active={record.active} /></div> },
    ],
    defaults: { parent_kind: '', child_kind: '', active: true },
    fields: (lookups) => [
      { name: 'parent_kind', label: 'Parent kind', type: 'select', required: true, options: kindOptions(lookups.kinds, false) },
      { name: 'child_kind', label: 'Child kind', type: 'select', required: true, options: kindOptions(lookups.kinds, false) },
      { name: 'active', label: 'Active', type: 'boolean' },
    ],
    buildPayload: (form) => ({ parent_kind: nullableId(form.parent_kind), child_kind: nullableId(form.child_kind), active: Boolean(form.active) }),
  },
  {
    key: 'statusDefinitions',
    label: 'Status Definitions',
    singular: 'status definition',
    searchFields: ['code', 'label', 'description'],
    columns: [
      { key: 'label', label: 'Definition', render: (record) => <div><p className="font-medium">{textValue(record.label)}</p><p className="text-xs text-muted-foreground">{textValue(record.code)}</p></div> },
      { key: 'description', label: 'Description' },
      { key: 'active', label: 'State', render: (record) => <div className="flex gap-1"><SystemBadge record={record} /><ActiveBadge active={record.active} /></div> },
    ],
    defaults: { code: '', label: '', description: '', is_system: false, active: true },
    fields: () => [
      { name: 'code', label: 'Code', required: true },
      { name: 'label', label: 'Label', required: true },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'is_system', label: 'System definition', type: 'boolean' },
      { name: 'active', label: 'Active', type: 'boolean' },
    ],
    buildPayload: makeDefinitionPayload,
  },
  {
    key: 'statuses',
    label: 'Statuses',
    singular: 'status',
    searchFields: ['code', 'label', 'definition_code', 'entity_type'],
    columns: [
      { key: 'label', label: 'Status', render: (record) => <div><p className="font-medium">{textValue(record.label)}</p><p className="text-xs text-muted-foreground">{textValue(record.code)}</p></div> },
      { key: 'entity_type', label: 'Entity', render: (record) => <Badge variant="outline">{workflowEntityTypeLabel(record.entity_type)}</Badge> },
      { key: 'flags', label: 'Flags', render: (record) => <div className="flex flex-wrap gap-1"><SystemBadge record={record} /><BoolBadge value={record.is_default} label="Default" /><BoolBadge value={record.is_terminal} label="Terminal" /><BoolBadge value={record.reason_required} label="Reason" /><ActiveBadge active={record.active} /></div> },
    ],
    defaults: { definition: '', entity_type: 'job', kind: NONE_VALUE, code: '', label: '', description: '', is_system: false, is_default: false, is_terminal: false, is_editable: true, reason_required: false, sorting_order: 0, active: true },
    fields: (lookups) => [
      { name: 'definition', label: 'Definition', type: 'select', required: true, options: lookups.statusDefinitions.map(optionFromRecord) },
      { name: 'entity_type', label: 'Entity type', type: 'select', required: true, options: ENTITY_TYPES },
      { name: 'kind', label: 'Kind scope', type: 'select', options: kindOptions(lookups.kinds) },
      { name: 'code', label: 'Code', required: true },
      { name: 'label', label: 'Label', required: true },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'sorting_order', label: 'Order', type: 'number' },
      { name: 'is_default', label: 'Default status', type: 'boolean' },
      { name: 'is_terminal', label: 'Terminal', type: 'boolean' },
      { name: 'is_editable', label: 'Editable', type: 'boolean' },
      { name: 'reason_required', label: 'Reason required', type: 'boolean' },
      { name: 'active', label: 'Active', type: 'boolean' },
    ],
    buildPayload: (form) => ({
      definition: nullableId(form.definition),
      entity_type: textValue(form.entity_type) || 'job',
      kind: nullableId(form.kind),
      code: textValue(form.code),
      label: textValue(form.label),
      description: textValue(form.description),
      is_system: Boolean(form.is_system),
      is_default: Boolean(form.is_default),
      is_terminal: Boolean(form.is_terminal),
      is_editable: Boolean(form.is_editable),
      reason_required: Boolean(form.reason_required),
      sorting_order: numberValue(form.sorting_order),
      active: Boolean(form.active),
    }),
  },
  {
    key: 'transitions',
    label: 'Status Transitions',
    singular: 'status transition',
    searchFields: ['entity_type', 'from_status_label', 'to_status_label', 'from_status_code', 'to_status_code'],
    columns: [
      { key: 'entity_type', label: 'Entity', render: (record) => <Badge variant="outline">{workflowEntityTypeLabel(record.entity_type)}</Badge> },
      { key: 'from_status_label', label: 'From', render: (record) => textValue(record.from_status_label) || 'Start' },
      { key: 'to_status_label', label: 'To', render: (record) => textValue(record.to_status_label || record.to_status) },
      { key: 'scope', label: 'Scope / Order', render: (record) => <ScopeCell record={record} /> },
      { key: 'requires_note', label: 'Rules', render: (record) => <div className="flex gap-1"><SystemBadge record={record} /><BoolBadge value={record.requires_note} label="Note" /><ActiveBadge active={record.active} /></div> },
    ],
    defaults: { entity_type: 'job', kind: NONE_VALUE, from_status: NONE_VALUE, to_status: '', requires_note: false, sorting_order: 0, active: true },
    fields: (lookups, form) => {
      const entityType = textValue(form?.entity_type) || 'job'
      return [
        { name: 'entity_type', label: 'Entity type', type: 'select', required: true, options: ENTITY_TYPES },
        { name: 'kind', label: 'Kind scope', type: 'select', options: kindOptions(lookups.kinds) },
        { name: 'from_status', label: 'From status', type: 'select', options: statusOptions(lookups.statuses, entityType) },
        { name: 'to_status', label: 'To status', type: 'select', required: true, options: statusOptions(lookups.statuses, entityType, false) },
        { name: 'sorting_order', label: 'Order', type: 'number' },
        { name: 'requires_note', label: 'Requires note', type: 'boolean' },
        { name: 'active', label: 'Active', type: 'boolean' },
      ]
    },
    buildPayload: (form) => ({
      entity_type: textValue(form.entity_type) || 'job',
      kind: nullableId(form.kind),
      from_status: nullableId(form.from_status),
      to_status: nullableId(form.to_status),
      requires_note: Boolean(form.requires_note),
      sorting_order: numberValue(form.sorting_order),
      active: Boolean(form.active),
    }),
  },
  {
    key: 'transitionActors',
    label: 'Status Actors',
    singular: 'status actor rule',
    searchFields: ['transition_label', 'actor_type', 'role_label', 'post_label', 'user_label', 'worker_skill_label', 'role_code'],
    columns: [
      { key: 'transition_label', label: 'Transition' },
      { key: 'actor_type', label: 'Actor', render: (record) => <Badge variant="outline">{textValue(record.actor_type)}</Badge> },
      { key: 'role_label', label: 'Target', render: actorTargetLabel },
      { key: 'active', label: 'State', render: (record) => <div className="flex gap-1"><SystemBadge record={record} /><ActiveBadge active={record.active} /></div> },
    ],
    defaults: { transition: '', actor_type: 'any_authenticated', role: '', post: '', user: '', worker_skill: '', active: true },
    fields: (lookups) => [
      { name: 'transition', label: 'Transition', type: 'select', required: true, options: statusTransitionOptions(lookups.transitions) },
      { name: 'actor_type', label: 'Actor type', type: 'select', required: true, options: ACTOR_TYPES },
      ...actorTargetFields(lookups),
      { name: 'active', label: 'Active', type: 'boolean' },
    ],
    buildPayload: buildActorPayload,
  },
  {
    key: 'stageDefinitions',
    label: 'Stage Definitions',
    singular: 'stage definition',
    searchFields: ['code', 'label', 'description'],
    columns: [
      { key: 'label', label: 'Definition', render: (record) => <div><p className="font-medium">{textValue(record.label)}</p><p className="text-xs text-muted-foreground">{textValue(record.code)}</p></div> },
      { key: 'description', label: 'Description' },
      { key: 'active', label: 'State', render: (record) => <div className="flex gap-1"><SystemBadge record={record} /><ActiveBadge active={record.active} /></div> },
    ],
    defaults: { code: '', label: '', description: '', is_system: false, active: true },
    fields: () => [
      { name: 'code', label: 'Code', required: true },
      { name: 'label', label: 'Label', required: true },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'is_system', label: 'System definition', type: 'boolean' },
      { name: 'active', label: 'Active', type: 'boolean' },
    ],
    buildPayload: makeDefinitionPayload,
  },
  {
    key: 'stages',
    label: 'Stages',
    singular: 'stage',
    searchFields: ['code', 'label', 'definition_code', 'kind_code'],
    columns: [
      { key: 'label', label: 'Stage', render: (record) => <div><p className="font-medium">{textValue(record.label)}</p><p className="text-xs text-muted-foreground">{textValue(record.code)}</p></div> },
      { key: 'kind_code', label: 'Kind', render: (record) => textValue(record.kind_code) || 'All kinds' },
      { key: 'flags', label: 'Flags', render: (record) => <div className="flex flex-wrap gap-1"><SystemBadge record={record} /><BoolBadge value={record.is_default} label="Default" /><BoolBadge value={record.is_terminal} label="Terminal" /><ActiveBadge active={record.active} /></div> },
    ],
    defaults: { definition: '', kind: NONE_VALUE, code: '', label: '', description: '', is_system: false, is_default: false, is_terminal: false, sorting_order: 0, active: true },
    fields: (lookups) => [
      { name: 'definition', label: 'Definition', type: 'select', required: true, options: lookups.stageDefinitions.map(optionFromRecord) },
      { name: 'kind', label: 'Kind scope', type: 'select', options: kindOptions(lookups.kinds) },
      { name: 'code', label: 'Code', required: true },
      { name: 'label', label: 'Label', required: true },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'sorting_order', label: 'Order', type: 'number' },
      { name: 'is_default', label: 'Default stage', type: 'boolean' },
      { name: 'is_terminal', label: 'Terminal', type: 'boolean' },
      { name: 'active', label: 'Active', type: 'boolean' },
    ],
    buildPayload: (form) => ({
      definition: nullableId(form.definition),
      kind: nullableId(form.kind),
      code: textValue(form.code),
      label: textValue(form.label),
      description: textValue(form.description),
      is_system: Boolean(form.is_system),
      is_default: Boolean(form.is_default),
      is_terminal: Boolean(form.is_terminal),
      sorting_order: numberValue(form.sorting_order),
      active: Boolean(form.active),
    }),
  },
  {
    key: 'stageTransitions',
    label: 'Stage Flow',
    singular: 'stage transition',
    searchFields: ['from_stage_label', 'to_stage_label', 'from_stage_code', 'to_stage_code', 'kind_code'],
    columns: [
      { key: 'kind_code', label: 'Kind', render: (record) => textValue(record.kind_code) || 'All kinds' },
      { key: 'from_stage_label', label: 'From', render: (record) => textValue(record.from_stage_label) || 'Start' },
      { key: 'to_stage_label', label: 'To', render: (record) => textValue(record.to_stage_label || record.to_stage) },
      { key: 'requires_note', label: 'Rules', render: (record) => <div className="flex gap-1"><SystemBadge record={record} /><BoolBadge value={record.requires_note} label="Note" /><ActiveBadge active={record.active} /></div> },
    ],
    defaults: { kind: NONE_VALUE, from_stage: NONE_VALUE, to_stage: '', requires_note: false, sorting_order: 0, active: true },
    fields: (lookups) => [
      { name: 'kind', label: 'Kind scope', type: 'select', options: kindOptions(lookups.kinds) },
      { name: 'from_stage', label: 'From stage', type: 'select', options: stageOptions(lookups.stages) },
      { name: 'to_stage', label: 'To stage', type: 'select', required: true, options: stageOptions(lookups.stages, false) },
      { name: 'sorting_order', label: 'Order', type: 'number' },
      { name: 'requires_note', label: 'Requires note', type: 'boolean' },
      { name: 'active', label: 'Active', type: 'boolean' },
    ],
    buildPayload: (form) => ({
      kind: nullableId(form.kind),
      from_stage: nullableId(form.from_stage),
      to_stage: nullableId(form.to_stage),
      requires_note: Boolean(form.requires_note),
      sorting_order: numberValue(form.sorting_order),
      active: Boolean(form.active),
    }),
  },
  {
    key: 'stageTransitionActors',
    label: 'Stage Actors',
    singular: 'stage actor rule',
    searchFields: ['transition_label', 'actor_type', 'role_label', 'post_label', 'user_label', 'worker_skill_label', 'role_code'],
    columns: [
      { key: 'transition_label', label: 'Transition' },
      { key: 'actor_type', label: 'Actor', render: (record) => <Badge variant="outline">{textValue(record.actor_type)}</Badge> },
      { key: 'role_label', label: 'Target', render: actorTargetLabel },
      { key: 'active', label: 'State', render: (record) => <div className="flex gap-1"><SystemBadge record={record} /><ActiveBadge active={record.active} /></div> },
    ],
    defaults: { transition: '', actor_type: 'any_authenticated', role: '', post: '', user: '', worker_skill: '', active: true },
    fields: (lookups) => [
      { name: 'transition', label: 'Transition', type: 'select', required: true, options: stageTransitionOptions(lookups.stageTransitions) },
      { name: 'actor_type', label: 'Actor type', type: 'select', required: true, options: ACTOR_TYPES },
      ...actorTargetFields(lookups),
      { name: 'active', label: 'Active', type: 'boolean' },
    ],
    buildPayload: buildActorPayload,
  },
]

const resourceByKey = Object.fromEntries(RESOURCE_CONFIGS.map((config) => [config.key, config])) as Record<ResourceKey, ResourceConfig>
const workflowResourceApi = jobWorkflowApi as unknown as Record<ResourceKey, WorkflowResourceApi>
const MODULE_FILTERED_RESOURCE_KEYS = new Set<ResourceKey>([
  'statuses',
  'transitions',
  'transitionActors',
  'stages',
  'stageTransitions',
  'stageTransitionActors',
])
const WORKFLOW_TAB_ALIASES: Record<string, ResourceKey> = {
  'status-flow': 'transitions',
  'status-transition': 'transitions',
  'status-transitions': 'transitions',
}

const isResourceKey = (value: string): value is ResourceKey => value in resourceByKey
const isQuickFilterValue = (value: string): value is QuickFilterValue => (
  QUICK_FILTERS.some((filter) => filter.value === value)
)

const resolveInitialQuickFilter = (_pathname: string, search: string): QuickFilterValue => {
  const params = new URLSearchParams(search)
  const filter = textValue(params.get('filter'))
  if (filter && isQuickFilterValue(filter) && (ENTITY_WORKFLOW_FILTERS as readonly string[]).includes(filter)) {
    return filter
  }

  return 'job'
}

const resolveInitialActiveKey = (pathname: string, search: string): ResourceKey => {
  if (pathname.includes('/job/status-transitions')) {
    return 'transitions'
  }

  const tab = new URLSearchParams(search).get('tab') || ''
  const normalizedTab = tab.trim()
  if (!normalizedTab) {
    return 'kinds'
  }

  return WORKFLOW_TAB_ALIASES[normalizedTab] || (isResourceKey(normalizedTab) ? normalizedTab : 'kinds')
}

const normalizeFormFromRecord = (config: ResourceConfig, record: WorkflowRecord): WorkflowFormState => {
  const next = { ...config.defaults }
  for (const key of Object.keys(next)) {
    const value = record[key]
    if (value === null || value === undefined || value === '') {
      next[key] = typeof next[key] === 'boolean' ? Boolean(next[key]) : next[key] === NONE_VALUE ? NONE_VALUE : ''
    } else if (typeof next[key] === 'boolean') {
      next[key] = Boolean(value)
    } else {
      next[key] = String(value)
    }
  }
  if (next.actor_type === 'worker_skill' && !textValue(next.worker_skill) && record.role != null) {
    next.worker_skill = String(record.role)
  }
  return next
}

function FieldControl({
  field,
  value,
  onChange,
}: {
  field: FieldConfig
  value: string | boolean | number | null
  onChange: (value: string | boolean) => void
}) {
  if (field.type === 'boolean') {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 px-3 py-2">
        <Label className="text-xs font-medium">{field.label}</Label>
        <Switch checked={Boolean(value)} onCheckedChange={onChange} />
      </div>
    )
  }

  if (field.type === 'select') {
    if (field.searchable) {
      return (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">{field.label}</Label>
          <SearchableSelect
            value={textValue(value)}
            onValueChange={onChange}
            onOpenChange={field.onOpenChange}
            onSearchChange={field.onSearchChange}
            options={field.options ?? []}
            placeholder={field.loading ? `Loading ${field.label.toLowerCase()}...` : `Select ${field.label.toLowerCase()}`}
            searchPlaceholder={field.searchPlaceholder ?? `Search ${field.label.toLowerCase()}`}
            emptyText={field.loading ? `Loading ${field.label.toLowerCase()}...` : field.emptyText}
            emptySearchText={field.loading ? `Loading ${field.label.toLowerCase()}...` : field.emptySearchText}
            disabled={field.disabled}
            ariaLabel={field.label}
          />
        </div>
      )
    }

    return (
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">{field.label}</Label>
        <Select value={textValue(value)} onValueChange={onChange} disabled={field.disabled}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {(field.options ?? []).map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }

  if (field.type === 'textarea') {
    return (
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">{field.label}</Label>
        <Textarea value={textValue(value)} onChange={(event) => onChange(event.target.value)} rows={3} />
      </div>
    )
  }

  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">{field.label}</Label>
      <Input
        type={field.type === 'number' ? 'number' : 'text'}
        value={textValue(value)}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

const roleToOption = (role: OrgRole): FieldOption => {
  const levelLabel = role.role_level_label ? ` (${role.role_level_label})` : role.role_level ? ` (L${role.role_level})` : ''
  const label = `${role.name}${levelLabel}`
  return {
    value: String(role.id),
    label,
    keywords: `${label} ${role.unique_id ?? ''} ${role.service_instance_name ?? ''}`,
  }
}

const postToOption = (post: AssignedRolePost): FieldOption => {
  const userName = textValue(post.user_name || post.user_data?.name) || 'Unknown user'
  const roleName = textValue(post.roles_name || post.roles_data?.roles_name) || 'Role'
  const label = `${userName} - ${roleName}`
  return {
    value: String(post.id),
    label,
    keywords: `${label} ${post.roles_data?.unique_id ?? ''}`,
  }
}

const userToOption = (user: OrgUserAdminRecord): FieldOption => {
  return {
    value: String(user.user_id),
    label: getUserDropdownLabel(user, user.user_id),
    keywords: getUserDropdownKeywords(user),
  }
}

const catalogSkillToOption = (skill: CatalogRole): FieldOption => {
  const levelLabel = skill.role_level_label ? ` (${skill.role_level_label})` : skill.role_level ? ` (L${skill.role_level})` : ''
  const label = `${skill.name}${levelLabel}`
  return {
    value: String(skill.id),
    label,
    keywords: `${label} ${skill.unique_id ?? ''} ${skill.role_group_name ?? ''} ${skill.service_instance_name ?? ''}`,
  }
}

function JobWorkflowsPage() {
  const location = useLocation()
  const labels = useWorkspaceLabels()
  const portalWorkflowKind = resolvePortalWorkflowKind(location.pathname, labels.mode)
  const [searchParams, setSearchParams] = useSearchParams()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const queryClient = useQueryClient()
  const [activeKey, setActiveKey] = useState<ResourceKey>(() => resolveInitialActiveKey(location.pathname, location.search))
  const [search, setSearch] = useState('')
  const [quickFilter, setQuickFilter] = useState<QuickFilterValue>(() => resolveInitialQuickFilter(location.pathname, location.search))
  const [editingRecord, setEditingRecord] = useState<WorkflowRecord | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [formState, setFormState] = useState<WorkflowFormState>(resourceByKey.kinds.defaults)
  const [deleteRecord, setDeleteRecord] = useState<WorkflowRecord | null>(null)
  const [actorLookupSearch, setActorLookupSearch] = useState<Record<ActorLookupField, string>>({
    role: '',
    post: '',
    user: '',
    worker_skill: '',
  })

  const queryParams = { page_size: 500 }
  const workflowKind: JobWorkflowKind = portalWorkflowKind
  const visibleQuickFilters = entityQuickFilters(portalWorkflowKind)
  const kindsQuery = useQuery({ queryKey: ['job-workflow', 'kinds'], queryFn: () => jobWorkflowApi.kinds.list(queryParams) })
  const childRulesQuery = useQuery({ queryKey: ['job-workflow', 'childRules'], queryFn: () => jobWorkflowApi.childRules.list(queryParams) })
  const statusDefinitionsQuery = useQuery({ queryKey: ['job-workflow', 'statusDefinitions'], queryFn: () => jobWorkflowApi.statusDefinitions.list(queryParams) })
  const statusesQuery = useQuery({ queryKey: ['job-workflow', 'statuses', workflowKind], queryFn: () => jobWorkflowApi.listStatusesByKind(workflowKind, queryParams) })
  const transitionsQuery = useQuery({ queryKey: ['job-workflow', 'transitions', workflowKind], queryFn: () => jobWorkflowApi.listTransitionsByKind(workflowKind, queryParams) })
  const transitionActorsQuery = useQuery({ queryKey: ['job-workflow', 'transitionActors', workflowKind], queryFn: () => jobWorkflowApi.listTransitionActorsByKind(workflowKind, queryParams) })
  const stageDefinitionsQuery = useQuery({ queryKey: ['job-workflow', 'stageDefinitions'], queryFn: () => jobWorkflowApi.stageDefinitions.list(queryParams) })
  const stagesQuery = useQuery({ queryKey: ['job-workflow', 'stages', workflowKind], queryFn: () => jobWorkflowApi.listStagesByKind(workflowKind, queryParams) })
  const stageTransitionsQuery = useQuery({ queryKey: ['job-workflow', 'stageTransitions', workflowKind], queryFn: () => jobWorkflowApi.listStageTransitionsByKind(workflowKind, queryParams) })
  const stageTransitionActorsQuery = useQuery({ queryKey: ['job-workflow', 'stageTransitionActors', workflowKind], queryFn: () => jobWorkflowApi.listStageTransitionActorsByKind(workflowKind, queryParams) })
  const formActorType = textValue(formState.actor_type)
  const actorLookupBaseEnabled = formOpen
    && (activeKey === 'transitionActors' || activeKey === 'stageTransitionActors')
    && Boolean(serviceInstanceId)
  const shouldLoadActorLookup = (fieldName: ActorLookupField) => (
    actorLookupBaseEnabled
    && formActorType === fieldName
  )

  const roleActorOptionsQuery = useQuery({
    queryKey: ['job-workflow', 'actor-options', 'roles', serviceInstanceId, actorLookupSearch.role],
    queryFn: async () => {
      const page = await orgRolesApi.getPage({
        serviceInstanceId,
        page: 1,
        pageSize: 100,
        search: actorLookupSearch.role,
      })
      return page.results.map(roleToOption)
    },
    enabled: shouldLoadActorLookup('role'),
    staleTime: 30_000,
  })
  const postActorOptionsQuery = useQuery({
    queryKey: ['job-workflow', 'actor-options', 'posts', serviceInstanceId, actorLookupSearch.post],
    queryFn: async () => {
      const page = await postsApi.getAssignedRolesPage({
        orgInstanceId: serviceInstanceId!,
        serviceInstanceId: serviceInstanceId!,
        page: 1,
        pageSize: 100,
        search: actorLookupSearch.post,
      })
      return page.results.map(postToOption)
    },
    enabled: shouldLoadActorLookup('post'),
    staleTime: 30_000,
  })
  const userActorOptionsQuery = useQuery({
    queryKey: ['job-workflow', 'actor-options', 'users', serviceInstanceId, actorLookupSearch.user],
    queryFn: async () => {
      const users = await orgUserAdminApi.list({
        search: actorLookupSearch.user,
        includeInactive: false,
        page: 1,
        pageSize: 100,
        serviceInstanceId,
        orgInstanceId: serviceInstanceId,
      })
      return users.map(userToOption)
    },
    enabled: shouldLoadActorLookup('user'),
    staleTime: 30_000,
  })
  const workerSkillActorOptionsQuery = useQuery({
    queryKey: ['job-workflow', 'actor-options', 'catalog-skills', serviceInstanceId],
    queryFn: async () => {
      const catalogSkills = await catalogRolesApi.list({
        serviceInstanceId,
        includeHidden: true,
      })
      return catalogSkills.map(catalogSkillToOption)
    },
    enabled: shouldLoadActorLookup('worker_skill'),
    staleTime: 30_000,
  })

  const queryByKey = {
    kinds: kindsQuery,
    childRules: childRulesQuery,
    statusDefinitions: statusDefinitionsQuery,
    statuses: statusesQuery,
    transitions: transitionsQuery,
    transitionActors: transitionActorsQuery,
    stageDefinitions: stageDefinitionsQuery,
    stages: stagesQuery,
    stageTransitions: stageTransitionsQuery,
    stageTransitionActors: stageTransitionActorsQuery,
  }

  const lookups: LookupData = useMemo(() => ({
    kinds: kindsQuery.data?.results ?? [],
    statusDefinitions: statusDefinitionsQuery.data?.results ?? [],
    statuses: statusesQuery.data?.results ?? [],
    transitions: transitionsQuery.data?.results ?? [],
    stageDefinitions: stageDefinitionsQuery.data?.results ?? [],
    stages: stagesQuery.data?.results ?? [],
    stageTransitions: stageTransitionsQuery.data?.results ?? [],
    actorOptions: {
      roles: roleActorOptionsQuery.data ?? [],
      posts: postActorOptionsQuery.data ?? [],
      users: userActorOptionsQuery.data ?? [],
      workerSkills: workerSkillActorOptionsQuery.data ?? [],
    },
  }), [
    kindsQuery.data,
    postActorOptionsQuery.data,
    roleActorOptionsQuery.data,
    stageDefinitionsQuery.data,
    stageTransitionsQuery.data,
    stagesQuery.data,
    statusDefinitionsQuery.data,
    statusesQuery.data,
    transitionsQuery.data,
    userActorOptionsQuery.data,
    workerSkillActorOptionsQuery.data,
  ])

  const activeConfig = resourceByKey[activeKey]
  const activeQuery = queryByKey[activeKey]
  const updateWorkflowUrl = (next: { tab?: ResourceKey; filter?: QuickFilterValue }) => {
    const params = new URLSearchParams(searchParams)
    if (next.tab) {
      params.set('tab', next.tab)
    }
    if (next.filter) {
      if (isKindWorkflowFilter(next.filter)) {
        params.set('kind', next.filter)
        params.delete('filter')
      } else {
        params.set('filter', next.filter)
        params.delete('kind')
      }
    }
    setSearchParams(params, { replace: true })
  }
  const actorLookupLoadingByField: Record<ActorLookupField, boolean> = {
    role: roleActorOptionsQuery.isFetching,
    post: postActorOptionsQuery.isFetching,
    user: userActorOptionsQuery.isFetching,
    worker_skill: workerSkillActorOptionsQuery.isFetching,
  }
  const enhanceField = (field: FieldConfig): FieldConfig => {
    if (!isActorLookupField(field.name)) {
      return field
    }

    const label = field.label.toLowerCase()
    const pluralLabel = field.name === 'worker_skill' ? 'skills' : `${label}s`
    return {
      ...field,
      loading: actorLookupLoadingByField[field.name],
      disabled: !serviceInstanceId,
      searchPlaceholder: `Search ${pluralLabel}`,
      emptyText: !serviceInstanceId
        ? 'Select a service instance first.'
        : actorLookupLoadingByField[field.name]
          ? `Loading ${pluralLabel}...`
          : `No ${pluralLabel} found for this service instance.`,
      emptySearchText: !serviceInstanceId
        ? 'Select a service instance first.'
        : actorLookupLoadingByField[field.name]
          ? `Loading ${pluralLabel}...`
          : `No ${pluralLabel} found for this service instance.`,
      onOpenChange: (open) => {
        if (open) {
          setActorLookupSearch((current) => ({ ...current, [field.name]: '' }))
        }
      },
      onSearchChange: (value) => {
        setActorLookupSearch((current) => ({ ...current, [field.name]: value }))
      },
    }
  }
  const records = (activeQuery.data?.results ?? []) as unknown as WorkflowRecord[]
  const filteredRecords = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const shouldApplyModuleFilter = MODULE_FILTERED_RESOURCE_KEYS.has(activeKey)
    const matchingRecords = records.filter((record) => {
      if (shouldApplyModuleFilter && !quickFilterMatches(record, quickFilter, portalWorkflowKind)) {
        return false
      }
      if (!needle) {
        return true
      }
      return activeConfig.searchFields.some((fieldName) => textValue(record[fieldName]).toLowerCase().includes(needle))
    })
    return dedupeTransitionRecords(activeKey, matchingRecords)
  }, [activeConfig.searchFields, activeKey, portalWorkflowKind, quickFilter, records, search])

  const invalidateWorkflow = () => queryClient.invalidateQueries({ queryKey: ['job-workflow'] })

  const createMutation = useMutation({
    mutationFn: (payload: WorkflowRecord) => workflowResourceApi[activeKey].create(payload),
    onSuccess: () => {
      toast.success(`${activeConfig.singular} created`)
      invalidateWorkflow()
      setFormOpen(false)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: WorkflowRecord }) => workflowResourceApi[activeKey].update(id, payload),
    onSuccess: () => {
      toast.success(`${activeConfig.singular} updated`)
      invalidateWorkflow()
      setFormOpen(false)
      setEditingRecord(null)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const deleteMutation = useMutation({
    mutationFn: ({ key, id }: { key: ResourceKey; id: string | number }) => workflowResourceApi[key].delete(id),
    onSuccess: () => {
      toast.success(`${activeConfig.singular} deleted`)
      invalidateWorkflow()
      setDeleteRecord(null)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const toggleActiveMutation = useMutation({
    mutationFn: ({ key, id, active }: { key: ResourceKey; id: string | number; active: boolean }) =>
      workflowResourceApi[key].update(id, { active }),
    onSuccess: (_data, variables) => {
      toast.success(`${activeConfig.singular} ${variables.active ? 'enabled' : 'disabled'}`)
      invalidateWorkflow()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const openCreate = () => {
    setEditingRecord(null)
    setFormState(buildScopedCreateDefaults(activeConfig, quickFilter, portalWorkflowKind, lookups))
    setFormOpen(true)
  }

  const openEdit = (record: WorkflowRecord) => {
    if (isProtectedRecord(record)) {
      toast.info('System default workflow records are read-only. Create a scoped/custom override instead.')
      return
    }
    setEditingRecord(record)
    setFormState(normalizeFormFromRecord(activeConfig, record))
    setFormOpen(true)
  }

  const submitForm = () => {
    const fields = activeConfig.fields(lookups, formState).filter((field) => (
      shouldShowScopedField(field, activeKey, Boolean(editingRecord))
      && (!field.visibleWhen || field.visibleWhen(formState))
    ))
    const missing = fields.find((field) => (field.required || field.requiredWhen?.(formState)) && !textValue(formState[field.name]))
    if (missing) {
      toast.error(`${missing.label} is required`)
      return
    }
    const payload = activeConfig.buildPayload(formState)
    if (editingRecord?.id != null) {
      updateMutation.mutate({ id: editingRecord.id, payload })
      return
    }
    createMutation.mutate(payload)
  }

  const isSaving = createMutation.isPending || updateMutation.isPending
  const entityLabelLower = labels.entityPluralLower
  const jobsHref = withServiceInstanceId(labels.basePath, serviceInstanceId)
  const workflowScopeLabel = primaryEntityFilterLabel(portalWorkflowKind)

  return (
    <div className="space-y-5 pb-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <Button variant="ghost" size="sm" className="-ml-2 h-8 px-2 text-xs" asChild>
            <Link to={jobsHref}>
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
              {`Back to ${entityLabelLower}`}
            </Link>
          </Button>
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg border bg-background">
              <GitBranch className="h-4 w-4" />
            </span>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Workflow management</h1>
              <p className="text-sm text-muted-foreground">
                Configure statuses, transitions, actors, and flows for {workflowScopeLabel.toLowerCase()}s, assignments, workday entries, expenses, and inventory.
              </p>
            </div>
          </div>
        </div>
        <Button size="sm" className="rounded-lg" onClick={openCreate}>
          <Plus className="mr-1.5 h-4 w-4" />
          Add {activeConfig.singular}
        </Button>
      </div>

      <Tabs
        value={activeKey}
        onValueChange={(value) => {
          const nextKey = value as ResourceKey
          setActiveKey(nextKey)
          setSearch('')
          updateWorkflowUrl({ tab: nextKey, filter: quickFilter })
        }}
      >
        <TabsList className="flex h-auto flex-wrap justify-start gap-1 rounded-lg bg-muted/60 p-1">
          {RESOURCE_CONFIGS.map((config) => (
            <TabsTrigger key={config.key} value={config.key} className="h-8 text-xs">
              {config.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {RESOURCE_CONFIGS.map((config) => (
          <TabsContent key={config.key} value={config.key} className="mt-4">
            <Card className="rounded-lg">
              <CardHeader className="space-y-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle className="text-base">{config.label}</CardTitle>
                  <div className="relative w-full sm:w-72">
                    <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input value={search} onChange={(event) => setSearch(event.target.value)} className="pl-8" placeholder="Search" />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {visibleQuickFilters.map((filter) => {
                    const selected = quickFilter === filter.value
                    return (
                      <button
                        key={filter.value}
                        type="button"
                        onClick={() => {
                          setQuickFilter(filter.value)
                          updateWorkflowUrl({ tab: activeKey, filter: filter.value })
                        }}
                        className={cn(
                          'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                          selected
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground',
                        )}
                      >
                        {filter.label}
                      </button>
                    )
                  })}
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {config.columns.map((column) => <TableHead key={column.key}>{column.label}</TableHead>)}
                        <TableHead className="w-[180px] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activeQuery.isLoading ? (
                        <TableRow><TableCell colSpan={config.columns.length + 1} className="h-24 text-center text-sm text-muted-foreground">Loading...</TableCell></TableRow>
                      ) : filteredRecords.length === 0 ? (
                        <TableRow><TableCell colSpan={config.columns.length + 1} className="h-24 text-center text-sm text-muted-foreground">No records found.</TableCell></TableRow>
                      ) : filteredRecords.map((record) => (
                        <TableRow key={String(record.id)} className={record.active === false ? 'bg-muted/30 opacity-70' : undefined}>
                          {config.columns.map((column) => (
                            <TableCell key={column.key} className={cn(column.key === 'description' && 'max-w-[360px] truncate')}>
                              {column.render ? column.render(record) : textValue(record[column.key])}
                            </TableCell>
                          ))}
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {'active' in record && record.id != null ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 px-2 text-xs"
                                  disabled={toggleActiveMutation.isPending}
                                  title={record.active === false ? 'Enable' : 'Disable'}
                                  onClick={() => {
                                    if (record.id != null) {
                                      toggleActiveMutation.mutate({
                                        key: activeKey,
                                        id: record.id,
                                        active: record.active === false,
                                      })
                                    }
                                  }}
                                >
                                  {record.active === false ? 'Enable' : 'Disable'}
                                </Button>
                              ) : null}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                disabled={isProtectedRecord(record)}
                                title={isProtectedRecord(record) ? 'System default records are read-only' : 'Edit'}
                                onClick={() => openEdit(record)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                disabled={isProtectedRecord(record)}
                                title={isProtectedRecord(record) ? 'System default records cannot be deleted' : 'Delete'}
                                onClick={() => setDeleteRecord(record)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingRecord ? 'Edit' : 'Add'} {activeConfig.singular}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            {activeConfig.fields(lookups, formState).filter((field) => (
              shouldShowScopedField(field, activeKey, Boolean(editingRecord))
              && (!field.visibleWhen || field.visibleWhen(formState))
            )).map((baseField) => {
              const field = enhanceField(scopeFieldOptions(baseField, activeKey, quickFilter, portalWorkflowKind, lookups, Boolean(editingRecord)))
              return (
                <div key={field.name} className={field.type === 'textarea' ? 'sm:col-span-2' : undefined}>
                  <FieldControl
                    field={field}
                    value={formState[field.name]}
                    onChange={(value) => setFormState((current) => {
                      if (field.name === 'actor_type') {
                        setActorLookupSearch({ role: '', post: '', user: '', worker_skill: '' })
                        return { ...current, actor_type: value, role: '', post: '', user: '', worker_skill: '' }
                      }
                      if (activeKey === 'transitions' && field.name === 'entity_type') {
                        const nextEntityType = textValue(value)
                        return {
                          ...current,
                          entity_type: nextEntityType,
                          kind: getKindIdForWorkflowFilter(lookups, portalWorkflowKind),
                          from_status: NONE_VALUE,
                          to_status: '',
                        }
                      }
                      if (activeKey === 'statuses' && ['definition', 'kind', 'entity_type'].includes(field.name)) {
                        const next = { ...current, [field.name]: value }
                        if (field.name === 'entity_type') {
                          next.kind = getKindIdForWorkflowFilter(lookups, portalWorkflowKind)
                        }
                        return {
                          ...next,
                          ...buildStatusAutoFields(next, lookups),
                        }
                      }
                      return { ...current, [field.name]: value }
                    })}
                  />
                </div>
              )
            })}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={submitForm} disabled={isSaving}>{isSaving ? 'Saving...' : 'Save'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteRecord)} onOpenChange={(open) => !open && setDeleteRecord(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {activeConfig.singular}?</AlertDialogTitle>
            <AlertDialogDescription>
              {isProtectedRecord(deleteRecord)
                ? 'System default workflow records cannot be deleted.'
                : `${getRecordLabel(deleteRecord ?? {}, activeConfig.singular)} will be removed.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isProtectedRecord(deleteRecord)}
              onClick={() => {
                if (deleteRecord?.id != null) {
                  deleteMutation.mutate({ key: activeKey, id: deleteRecord.id })
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default JobWorkflowsPage
